import { useDispatch } from 'react-redux';
import { Message } from '@blink/components/src/constants/messages';
import { WalletHelper } from '@blink/components/src/utils';
import Logger from '@blink/components/src/utils/logger';

import { OperationStatusType } from '@/features/Modals/ui/OperationStatusModal';
import { useEthers } from '@/shared/hooks/useEthers';
import { toast } from '@/shared/hooks/useToast';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { getAssetsToSupplyAction, getUsersSupplies } from '@/shared/store/supplies/actions';
import { withdrawalErrorAction } from '@/shared/store/errors/actions';
import { compareBigNumbers } from '@/shared/utils/helpers';

type MakeWithdrawalProps = {
    amount: string;
    agreementAddress: string;
    tokenAddress: string;
    totalPosition: string;
    poolBalance: string;
    decimals: number;
};

export const useWithdrawalLpToken = () => {
    const dispatch = useDispatch();
    const { withdrawLpToken } = useEthers();

    const handleError = (e: any) => {
        let description = Message.SOMETHING_WENT_WRONG;
        let modalType = OperationStatusType.Error;

        if (e?.code === 'ACTION_REJECTED' || e?.message?.includes('Cancelled')) {
            modalType = OperationStatusType.Cancelled;
            description = Message.TRANSACTION_CANCELLED;
        }

        if (e?.message?.includes('Request expired')) {
            modalType = OperationStatusType.Timeout;
            description = Message.REQUEST_EXPIRED;
        }

        Logger.error(`useWithdrawalLpToken:::withdraw:${description}`, { error: e });

        dispatch(
            showModalAction({
                modalName: ModalName.OPERATION_STATUS_MODAL,
                modalProps: {
                    type: modalType,
                    description,
                },
            }),
        );
    };

    const makeWithdrawal = async ({
        amount,
        agreementAddress,
        tokenAddress,
        totalPosition,
        poolBalance,
        decimals,
    }: MakeWithdrawalProps) => {
        const commonVariables = {
            amount: amount?.toString() || '0',
            agreementAddress,
            tokenAddress,
            orderPlacedCallback: () =>
                dispatch(
                    showModalAction({
                        modalName: ModalName.OPERATION_STATUS_MODAL,
                        modalProps: {
                            type: OperationStatusType.Success,
                            description: Message.SUPPLY_ORDER_PLACED,
                        },
                    }),
                ),
            receiptReceivedCallback: (data: any) => {
                if (data.status === 1) {
                    toast({
                        title: Message.WITHDRAWAL_TRANSACTION_COMPLETED,
                        variant: 'success',
                    });
                    dispatch(getUsersSupplies());
                    dispatch(getAssetsToSupplyAction());

                    return;
                }

                Logger.error('useWithdrawalLpToken:::withdraw:receiptReceivedCallback', {
                    error: data,
                });

                throw data.error || Message.SOMETHING_WENT_WRONG;
            },
        };

        try {
            const result: number = compareBigNumbers(totalPosition, poolBalance, decimals);
            const min1: string = result < 0 ? totalPosition : poolBalance;

            const res: number = compareBigNumbers(min1, amount!.toString(), decimals);
            const amountToWithdraw = res < 0 ? min1 : amount!.toString();

            await withdrawLpToken({
                ...commonVariables,
                amount: amountToWithdraw,
            });

            return;
        } catch (e: any) {
            const badMessage = WalletHelper.messageInCaseOfNotCorrectNetwork(e);

            dispatch(
                withdrawalErrorAction({
                    message: badMessage || Message.SOMETHING_WENT_WRONG,
                }),
            );

            handleError(e);
        }
    };

    return {
        makeWithdrawal,
    };
};
